/**
 * Novo atendimento em 2 toques (11.1 função 2): tocar no horário livre e tocar na cliente.
 * O serviço vem do último que ela fez (ou do escolhido nas opções acima da lista).
 * Cliente nova se cria só com nome e telefone.
 */
import { type Location, formatDayShort, formatDuration, formatHourOf, formatMoneyShort, matchClient, utcToZoned } from '@lindeza/core';
import { useClientPicker, useProfessional, useRuntime, useServices } from '@lindeza/core/hooks';
import { useMemo, useState } from 'react';
import { toast, toastError } from '../../app/ui';
import { messageFor, openWhatsapp, useMessageCtx } from '../../app/useMessages';
import { IconSearch, IconUserRoundPlus } from '../../components/Icons';
import { PhoneInput } from '../../components/PhoneInput';
import { Avatar, Field, Segmented, Sheet } from '../../components/ui';

export function NewAppointmentSheet({ start, clientId, onClose }: { start: string; clientId?: string; onClose: () => void }) {
  const rt = useRuntime();
  const prof = useProfessional();
  const services = useServices();
  const picks = useClientPicker();
  const ctx = useMessageCtx();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  /* Vários serviços no mesmo horário; a ordem é a dos toques. */
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [location, setLocation] = useState<Location>(prof?.serves_at_studio === false ? 'home' : 'studio');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const serviceById = useMemo(() => new Map((services ?? []).map((s) => [s.id, s])), [services]);
  const defaultService = services?.[0] ?? null;
  const chosen = serviceIds.map((id) => serviceById.get(id)).filter((s) => s !== undefined);
  const totalMin = chosen.reduce((soma, s) => soma + s.duration_min, 0);
  const totalCents = chosen.reduce((soma, s) => soma + s.price_cents, 0);

  const toggleService = (id: string) =>
    setServiceIds((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));

  const filtered = useMemo(() => {
    const list = (picks ?? []).filter((p) => matchClient(p.client, query));
    // Veio da ficha da cliente: ela fica em primeiro.
    const i = clientId ? list.findIndex((p) => p.client.id === clientId) : -1;
    if (i > 0) list.unshift(...list.splice(i, 1));
    return list.slice(0, 40);
  }, [picks, query, clientId]);

  async function book(input: { clientId?: string; newClient?: { name: string; whatsapp?: string }; lastServiceId?: string | null }) {
    /*
     * Sem chip marcado, vale o serviço de sempre dela (ou o primeiro do
     * catálogo): é o caminho de um toque, e não pode piorar por causa da
     * escolha múltipla.
     */
    const escolhidos = chosen.length > 0 ? chosen : [];
    const service = escolhidos[0] ?? (input.lastServiceId ? serviceById.get(input.lastServiceId) : null) ?? defaultService;
    if (!service) {
      toast('Cadastre um serviço em Ajustes primeiro.', { tone: 'error' });
      return;
    }
    setBusy(true);
    try {
      const { appointment, client, conflicts } = await rt.actions.createAppointment({
        ...(input.clientId ? { clientId: input.clientId } : {}),
        ...(input.newClient ? { newClient: input.newClient } : {}),
        serviceId: service.id,
        ...(escolhidos.length > 1 ? { serviceIds: escolhidos.map((s) => s.id) } : {}),
        start,
        location,
      });
      onClose();
      if (conflicts.length > 0) {
        toast(`Atenção: choque com outro atendimento às ${formatHourOf(conflicts[0]!.start_utc, tz)}.`, { tone: 'error' });
      }
      const text = ctx ? messageFor(ctx, 'confirmation', appointment, client, service) : '';
      toast(`Marcado: ${client.name.split(' ')[0]} · ${formatHourOf(appointment.start_utc, tz)}`, {
        action: ctx ? { label: 'Avisar', run: () => openWhatsapp(client.whatsapp, text) } : undefined,
        duration: 7000,
      });
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  const local = utcToZoned(start, tz);

  return (
    <Sheet
      title={`Novo atendimento, ${formatDayShort(local.date).split(',')[0]} ${formatHourOf(start, tz)}`}
      sub="Toque na cliente: marca o serviço de sempre dela."
      onClose={onClose}
    >
      <div className="stack stack--lg">
        <div>
          <div className="at-sec-title" style={{ marginBottom: 6 }}>
            Outro serviço?
          </div>
          <div className="chips" role="group" aria-label="Serviço">
            {(services ?? []).map((s) => (
              <button
                key={s.id}
                type="button"
                className="chip"
                aria-pressed={serviceIds.includes(s.id)}
                onClick={() => toggleService(s.id)}
              >
                {s.name}
              </button>
            ))}
          </div>
          {/* Com mais de um, a conta não é óbvia: mostra a soma antes de marcar. */}
          {chosen.length > 1 && (
            <p className="at-sec-sub" aria-live="polite">
              {chosen.map((s) => s.name).join(' + ')} · {formatDuration(totalMin)} · {formatMoneyShort(totalCents)}
            </p>
          )}
        </div>

        {prof?.serves_at_home && prof.serves_at_studio && (
          <Segmented
            label="Local"
            value={location}
            onChange={setLocation}
            options={[
              { value: 'studio', label: 'No espaço' },
              { value: 'home', label: 'Domicílio' },
            ]}
          />
        )}

        {creating ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              void book({ newClient: { name, ...(phone ? { whatsapp: phone } : {}) } });
            }}
          >
            <Field label="Nome" htmlFor="nc-name">
              <input id="nc-name" className="input" autoFocus autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="WhatsApp" htmlFor="nc-phone" hint="DDD + número">
              <PhoneInput id="nc-phone" className="input" value={phone} onChange={setPhone} />
            </Field>
            <button className="btn btn--primary btn--lg btn--block" disabled={busy || !name.trim()}>
              Marcar{' '}
              {chosen.length > 0
                ? chosen.map((s) => s.name.toLowerCase()).join(' + ')
                : (defaultService?.name.toLowerCase() ?? '')}
            </button>
            <button type="button" className="btn btn--ghost btn--block" onClick={() => setCreating(false)}>
              Escolher cliente da lista
            </button>
          </form>
        ) : (
          <>
            <div className="row">
              <div className="grow" style={{ position: 'relative' }}>
                <IconSearch style={{ position: 'absolute', left: 12, top: 12, width: 22, height: 22, color: 'var(--text-muted)' }} />
                <input
                  className="input"
                  style={{ paddingLeft: 42 }}
                  placeholder="Nome ou telefone"
                  aria-label="Buscar cliente"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <button
                className="btn btn--outline"
                onClick={() => {
                  setCreating(true);
                  if (query && !/\d{4,}/.test(query)) setName(query);
                  if (/\d{4,}/.test(query)) setPhone(query);
                }}
              >
                <IconUserRoundPlus /> Cliente nova
              </button>
            </div>
            <ul className="list" aria-label="Clientes">
              {filtered.length === 0 && (
                <li style={{ padding: 'var(--space-lg)' }} className="muted">
                  {picks && picks.length === 0 ? 'Nenhuma cliente ainda. Toque em Nova.' : 'Ninguém com esse nome. Toque em Nova.'}
                </li>
              )}
              {filtered.map(({ client, lastServiceId }) => {
                // Com chips marcados, a linha mostra a soma deles; senão, o
                // serviço de sempre dela.
                const s = chosen[0] ?? (lastServiceId ? serviceById.get(lastServiceId) : null) ?? defaultService;
                return (
                  <li key={client.id}>
                    <button className="list-item" disabled={busy} onClick={() => void book({ clientId: client.id, lastServiceId })}>
                      <Avatar name={client.name} />
                      <span className="grow" style={{ minWidth: 0 }}>
                        <span className="title ellipsis" style={{ display: 'block' }}>
                          {client.name}
                        </span>
                        {s && (
                          <span className="meta">
                            {chosen.length > 1 ? chosen.map((x) => x.name).join(' + ') : s.name},{' '}
                            {formatDuration(chosen.length > 1 ? totalMin : s.duration_min)},{' '}
                            {formatMoneyShort(chosen.length > 1 ? totalCents : s.price_cents)}
                            {chosen.length === 0 && lastServiceId === s.id ? ', de sempre' : ''}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </Sheet>
  );
}
